import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet, Users, FileText, Package, FolderTree, Layers, CreditCard, ArrowRight, ClipboardList } from "lucide-react";
import { importAPI, adminAPI } from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  { key: "families", label: "Familles", icon: FolderTree, file: "familles.csv", desc: "25 familles de produits", endpoint: "families" },
  { key: "sub_families", label: "Sous-familles", icon: Layers, file: "sous_familles.csv", desc: "101 sous-familles", endpoint: "sub-families" },
  { key: "products", label: "Produits", icon: Package, file: "produits.csv", desc: "563 produits", endpoint: "products" },
  { key: "clients", label: "Clients & Prospects", icon: Users, file: "clients_prospects.csv", desc: "2241 contacts", endpoint: "clients" },
  { key: "documents_factures", label: "Factures", icon: FileText, file: "factures.csv", desc: "893 factures", endpoint: "documents" },
  { key: "documents_devis", label: "Devis", icon: FileText, file: "devis.csv", desc: "113 devis", endpoint: "documents" },
  { key: "documents_avoirs", label: "Avoirs", icon: FileText, file: "avoirs.csv", desc: "2 avoirs", endpoint: "documents" },
  { key: "lines", label: "Lignes de documents", icon: FileSpreadsheet, file: "lignes_documents.csv", desc: "1576 lignes", endpoint: "document-lines" },
  { key: "payments", label: "Règlements", icon: CreditCard, file: "règlements.csv", desc: "5382 paiements", endpoint: "payments" },
  { key: "product_contracts", label: "Contrats produits", icon: ClipboardList, file: "produit_client.csv", desc: "Contrats produit-client", endpoint: "product-contracts" },
];

function StepIcon({ status, index }) {
  if (status === "success") return <CheckCircle2 size={16} />;
  if (status === "error") return <AlertCircle size={16} />;
  return index + 1;
}

function PaymentMethodReviewPanel({ review }) {
  const [labels, setLabels] = useState(() => Object.fromEntries(review.map(r => [r.value, r.suggested_label || r.value])));
  const [added, setAdded] = useState({});
  if (!review?.length) return null;
  const add = async (value) => {
    try {
      await adminAPI.addPaymentMethod(value, labels[value]);
      setAdded(a => ({ ...a, [value]: true }));
      toast.success(`Mode « ${labels[value]} » ajouté à la liste`);
    } catch (e) {
      if (e.response?.status === 409) { setAdded(a => ({ ...a, [value]: true })); toast.info("Déjà dans la liste"); }
      else toast.error(e.response?.data?.detail || "Erreur");
    }
  };
  return (
    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 p-2.5" data-testid="payment-method-review">
      <p className="text-xs font-medium text-amber-800 mb-2">Modes de règlement absents de votre liste (la valeur brute est conservée sur chaque document) :</p>
      <div className="space-y-1.5">
        {review.map(r => (
          <div key={r.value} className="flex items-center gap-2 text-xs" data-testid={`pm-review-row-${r.value}`}>
            <span className="text-amber-900 flex-1 min-w-0">
              <strong>{r.count}</strong> document{r.count > 1 ? "s" : ""} portent le mode <span className="font-mono">« {r.value} »</span>
            </span>
            {added[r.value] ? (
              <Badge className="bg-emerald-100 text-emerald-700">Ajouté</Badge>
            ) : (
              <>
                <input
                  value={labels[r.value]}
                  onChange={e => setLabels(l => ({ ...l, [r.value]: e.target.value }))}
                  className="h-7 w-32 rounded border border-amber-300 px-2 text-xs"
                  data-testid={`pm-review-label-${r.value}`}
                  placeholder="Libellé"
                />
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => add(r.value)} data-testid={`pm-review-add-${r.value}`}>Ajouter à la liste</Button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function ImportWarningsPanel({ warnings, stepKey }) {
  const [open, setOpen] = useState(false);
  // Grouper par raison
  const byReason = warnings.reduce((acc, w) => {
    const r = w.reason || "inconnue";
    if (!acc[r]) acc[r] = [];
    acc[r].push(w);
    return acc;
  }, {});
  const reasonLabels = {
    missing_name: "Nom manquant",
    missing_number: "Numéro manquant",
    missing_identity: "Identité manquante",
    duplicate_name: "Nom déjà présent",
    duplicate_product: "Produit déjà présent",
    duplicate_client: "Client déjà présent",
    duplicate_document: "Document déjà présent",
    unmapped_family: "Famille non mappée",
    unmapped_sub_family: "Sous-famille non mappée",
    unmapped_client: "Client non mappé",
    unmapped_document: "Document parent non mappé",
    unmapped_product: "Produit non mappé",
  };
  return (
    <div className="mt-2" data-testid={`import-warnings-${stepKey}`}>
      <button
        type="button"
        className="text-xs font-medium text-amber-700 hover:text-amber-800 flex items-center gap-1"
        onClick={() => setOpen(o => !o)}
      >
        <AlertCircle size={12} />
        {warnings.length} avertissement{warnings.length > 1 ? "s" : ""} — {open ? "masquer" : "voir le détail"}
      </button>
      {open && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded max-h-64 overflow-y-auto">
          {Object.entries(byReason).map(([reason, items]) => (
            <div key={reason} className="mb-2">
              <div className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                {reasonLabels[reason] || reason} <span className="font-mono text-slate-500">({items.length})</span>
              </div>
              <ul className="mt-1 space-y-0.5">
                {items.slice(0, 20).map((w, i) => (
                  <li key={i} className="text-xs text-slate-700 font-mono" data-testid={`warning-${stepKey}-${reason}-${i}`}>
                    <span className="text-slate-400">L{w.row}</span>
                    {w.old_id ? <span className="text-slate-500"> old_id=<span className="text-slate-800">{w.old_id}</span></span> : null}
                    {w.hint ? <span className="text-slate-600"> — {w.hint}</span> : null}
                  </li>
                ))}
                {items.length > 20 && (
                  <li className="text-xs text-slate-500 italic">… et {items.length - 20} autre(s)</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function buildImportResultMessage(data) {
  const created = data.created ?? 0;
  const updated = data.updated ?? 0;
  const skipped = data.skipped ?? 0;
  const total = data.total ?? data.total_lines ?? 0;
  const warningsCount = (data.warnings || []).length;
  if (data.documents_updated != null) {
    const p = [`${data.documents_updated} documents mis à jour (${data.total_lines} lignes)`];
    if (data.unmapped_lines) p.push(`${data.unmapped_lines} ligne(s) sans doc parent`);
    if (data.unmapped_products) p.push(`${data.unmapped_products} produit(s) non mappé(s)`);
    return p.join(", ");
  }
  const parts = [`${created} créé(s)`];
  if (updated) parts.push(`${updated} mis à jour`);
  if (skipped) parts.push(`${skipped} ignoré(s)`);
  parts.push(`sur ${total} lignes`);
  if (warningsCount) parts.push(`${warningsCount} avertissement(s)`);
  if (data.unmapped_clients) parts.push(`${data.unmapped_clients} client(s) non mappé(s)`);
  return parts.join(", ");
}

function getStepBorderClass(status, isActive) {
  if (status === "success") return "border-emerald-300 bg-emerald-50/50";
  if (status === "error") return "border-red-300 bg-red-50/50";
  if (isActive) return "border-indigo-300 bg-indigo-50/30";
  return "border-slate-200 bg-white";
}

function getStepCircleClass(status, isActive) {
  if (status === "success") return "bg-emerald-500 text-white";
  if (status === "error") return "bg-red-500 text-white";
  if (isActive) return "bg-indigo-500 text-white";
  return "bg-slate-200 text-slate-500";
}

function StepCard({ step, index, result, isActive, onUpload, uploading }) {
  const fileRef = useRef(null);
  const status = result?.status;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(step, file);
    e.target.value = "";
  };

  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${getStepBorderClass(status, isActive)}`}
      data-testid={`import-step-${step.key}`}
    >
      <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${getStepCircleClass(status, isActive)}`}>
        <StepIcon status={status} index={index} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <step.icon size={16} className="text-slate-500 shrink-0" />
          <span className="font-medium text-sm text-slate-900">{step.label}</span>
          <Badge variant="outline" className="text-[10px] font-mono">{step.file}</Badge>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">{step.desc}</p>
        {result?.message && (
          <p className={`text-xs mt-1 font-medium ${status === "success" ? "text-emerald-600" : "text-red-600"}`}>
            {result.message}
          </p>
        )}
        {result?.data?.warnings?.length > 0 && (
          <ImportWarningsPanel warnings={result.data.warnings} stepKey={step.key} />
        )}
        {result?.data?.payment_method_review?.length > 0 && (
          <PaymentMethodReviewPanel review={result.data.payment_method_review} />
        )}
      </div>
      <div>
        <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
        {status === "success" ? (
          <Badge className="bg-emerald-100 text-emerald-700">Importe</Badge>
        ) : (
          <Button
            size="sm"
            variant={isActive ? "default" : "outline"}
            className={`text-xs ${isActive ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""}`}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`import-upload-${step.key}`}
          >
            {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
            {uploading ? "Import..." : "Charger CSV"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function ImportPage() {
  const [results, setResults] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    importAPI.status().then(r => setCounts(r.data)).catch(() => {});
  }, [results]);

  const handleUpload = async (step, file) => {
    setUploading(true);
    try {
      const res = await importAPI.upload(step.endpoint, file);
      const data = res.data;
      const msg = buildImportResultMessage(data);
      setResults(prev => ({ ...prev, [step.key]: { status: "success", message: msg, data } }));
      toast.success(`${step.label}: ${msg}`);
      // Auto advance
      const idx = STEPS.findIndex(s => s.key === step.key);
      if (idx < STEPS.length - 1) setCurrentStep(idx + 1);
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur d'import";
      setResults(prev => ({ ...prev, [step.key]: { status: "error", message: detail } }));
      toast.error(`${step.label}: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const complétédCount = Object.values(results).filter(r => r.status === "success").length;

  return (
    <div className="page-container" data-testid="import-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Import de donnees</h1>
        <p className="text-slate-500 mt-1">Importez vos donnees depuis votre ancien logiciel (CSV)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          <Card className="p-4 border border-slate-200 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Etapes d'import</h2>
                <p className="text-xs text-slate-500">Importez les fichiers dans l'ordre indique</p>
              </div>
              <Badge variant="outline" className="text-xs">
                {complétédCount}/{STEPS.length} termine(s)
              </Badge>
            </div>
            <div className="space-y-2">
              {STEPS.map((step, i) => (
                <StepCard
                  key={step.key}
                  step={step}
                  index={i}
                  result={results[step.key]}
                  isActive={i === currentStep}
                  onUpload={handleUpload}
                  uploading={uploading}
                />
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-4 border border-slate-200 bg-white">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Donnees actuelles</h3>
            {counts ? (
              <div className="space-y-2">
                {[
                  { label: "Familles", value: counts.families, icon: FolderTree },
                  { label: "Sous-familles", value: counts.sub_families, icon: Layers },
                  { label: "Produits", value: counts.products, icon: Package },
                  { label: "Clients/Prospects", value: counts.clients, icon: Users },
                  { label: "Documents", value: counts.documents, icon: FileText },
                  { label: "Paiements", value: counts.payments, icon: CreditCard },
                  { label: "Contrats produits", value: counts.product_contracts, icon: ClipboardList },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <item.icon size={14} className="text-slate-400" />
                      {item.label}
                    </div>
                    <span className="text-sm font-mono font-semibold text-slate-900">{item.value?.toLocaleString("fr-FR") ?? "—"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400">Chargement...</p>
            )}
          </Card>

          <Card className="p-4 border border-orange-200 bg-orange-50/50">
            <h3 className="text-sm font-semibold text-orange-800 mb-2">Ordre d'import</h3>
            <ol className="space-y-1.5 text-xs text-orange-700">
              <li className="flex items-center gap-1.5"><ArrowRight size={10} />1. Familles</li>
              <li className="flex items-center gap-1.5"><ArrowRight size={10} />2. Sous-familles</li>
              <li className="flex items-center gap-1.5"><ArrowRight size={10} />3. Produits</li>
              <li className="flex items-center gap-1.5"><ArrowRight size={10} />4. Clients & Prospects</li>
              <li className="flex items-center gap-1.5"><ArrowRight size={10} />5. Factures / Devis / Avoirs</li>
              <li className="flex items-center gap-1.5"><ArrowRight size={10} />6. Lignes de documents</li>
              <li className="flex items-center gap-1.5"><ArrowRight size={10} />7. Règlements</li>
              <li className="flex items-center gap-1.5"><ArrowRight size={10} />8. Contrats produits</li>
            </ol>
            <p className="text-[10px] text-orange-600 mt-2">L'ordre est important : les documents referencent les clients, les lignes referencent les documents et produits. Les contrats produits referencent les clients et les produits.</p>
          </Card>

          <Card className="p-4 border border-slate-200 bg-white">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Format CSV attendu</h3>
            <ul className="space-y-1 text-xs text-slate-500">
              <li>Separateur : <strong>point-virgule (;)</strong> ou virgule</li>
              <li>Encodage : <strong>UTF-8</strong></li>
              <li>Première ligne : <strong>en-têtes de colonnes</strong></li>
              <li>Les doublons sont automatiquement ignores</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
